/***
 *
 *   SIDEBAR ORG SWITCHER
 *   The sidebar's top slot. Ported from
 *   ui-reference/src/components/layouts/sidebar-workspace-switcher.tsx.
 *
 *   ADAPTATION (trap 6 in docs/ui-refresh/PLAN.md). There are no workspaces in
 *   this app. The nearest thing is the ORGANISATION: a user can belong to
 *   several, `AuthContext.switchAccount` moves between them, and the sidebar
 *   already carried that affordance — it was the `Avatar` + `HoverNav` pair
 *   this component replaces. So the reference's workspace switcher maps onto
 *   organisations rather than becoming a dead identity block.
 *
 *   Two things the reference has and this does not:
 *     - per-workspace unread counts. No such endpoint here, so no badge.
 *     - a "create workspace" item. Creating an organisation is not a thing a
 *       signed-in user does here; owners get a link to organisation settings
 *       instead, which is where the old Avatar pointed.
 *
 **********/

import { useContext } from 'react';
import { AuthContext } from 'app/auth';
import { NavLink } from 'react-router-dom';
import { Icon, cn, useNavigate } from 'components/lib';
import { useTranslate } from 'app/translations';
import { useSidebarContext } from 'context/SidebarContext';
import { ProjectMark } from 'components/layout/projectMark';
import { Menu } from 'components/dropdown/menu';
import Style from './app.tailwind.js';

export function SidebarOrgSwitcher() {
    const { _t } = useTranslate();
    const navigate = useNavigate();
    const { isCollapsed } = useSidebarContext();
    const { user, switchAccount } = useContext(AuthContext);

    const accounts = user?.accounts || [];
    const active = accounts.find((a) => a.id === user?.account_id) || accounts[0];
    const hasMultiple = accounts.length > 1;
    const isOwner = user?.permission === 'owner';

    const rowClass = cn(
        Style.orgRow,
        isCollapsed ? Style.orgRowCollapsed : Style.orgRowExpanded,
        (hasMultiple || isOwner) && Style.orgRowInteractive
    );

    // The accounts list rides along on the cached user, so it is normally
    // present on first paint. When it is not, hold the row's shape rather than
    // letting the sidebar header jump.
    if (!active)
        return (
            <div
                className={cn(Style.orgRow, isCollapsed ? Style.orgRowCollapsed : Style.orgRowExpanded)}
                aria-hidden>
                <span className={Style.orgSkeletonMark} />
                {!isCollapsed && <span className={Style.orgSkeletonLabel} />}
            </div>
        );

    const mark = (
        <ProjectMark
            name={active.name}
            filePath={active.file_path}
            className={Style.orgMark}
            fallbackClassName={Style.orgMarkFallback}
        />
    );

    const row = (
        <>
            {mark}
            {!isCollapsed && (
                <>
                    <span className={Style.orgName}>{active.name}</span>
                    {hasMultiple && (
                        <Icon
                            image="chevron-down"
                            size={16}
                            color="currentColor"
                            className={Style.orgChevron}
                        />
                    )}
                </>
            )}
        </>
    );

    if (hasMultiple)
        return (
            <Menu
                align="start"
                width="w-56"
                className="w-full"
                triggerClassName={rowClass}
                label={_t('organization.switch')}
                trigger={row}
                list={[
                    { label: _t('organization.switch'), heading: true },
                    { separator: true },
                    ...accounts.map((account) => ({
                        label: account.name,
                        checked: account.id === active.id,
                        action: () => account.id !== active.id && switchAccount(account.id),
                    })),
                    ...(isOwner
                        ? [
                              { separator: true },
                              {
                                  label: _t('organization.label'),
                                  icon: 'settings',
                                  action: () => navigate('/app/settings/organization'),
                              },
                          ]
                        : []),
                ]}
            />
        );

    // Single organisation: identity only. Owners can still reach its settings,
    // which is where the sidebar avatar used to link.
    if (isOwner)
        return (
            // NavLink rather than components/lib's Link: that one applies the
            // link colour ramp, which would tint the row against the indigo panel.
            <NavLink to="/app/settings/organization" className={rowClass}>
                {row}
            </NavLink>
        );

    return (
        <div className={rowClass} role="status" aria-label={_t('organization.label')}>
            {row}
        </div>
    );
}
